import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Filter, AlertTriangle, MessageCircle, QrCode, Search, Lock, Trash2, Loader2, Cpu, Code2 } from "lucide-react";
import { toast } from "sonner";
import { PayHoursDialog } from "@/components/PayHoursDialog";
import { RicaricaOreWidget } from "@/components/RicaricaOreWidget";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";
import { LicensePicker } from "@/components/LicensePicker";

const priorityColor = {
  critica: "bg-red-100 text-red-800 border-red-200",
  alta: "bg-amber-100 text-amber-800 border-amber-200",
  media: "bg-zinc-100 text-zinc-800 border-zinc-200",
  bassa: "bg-zinc-50 text-zinc-600 border-zinc-200",
};
const statusColor = {
  aperto: "bg-red-50 text-red-700 border-red-200",
  in_lavorazione: "bg-amber-50 text-amber-800 border-amber-200",
  risolto: "bg-emerald-50 text-emerald-800 border-emerald-200",
  chiuso: "bg-zinc-100 text-zinc-700 border-zinc-200",
  in_attesa_pagamento: "bg-amber-100 text-amber-900 border-amber-300",
};

const empty = {
  dispenser_serial: "",
  title: "",
  description: "",
  priority: "media",
  intervention_type: "hardware",
  license_id: "",
  assigned_to: "",
  reported_by: "",
};

export default function Tickets() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [dispensers, setDispensers] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [serialWarning, setSerialWarning] = useState("");
  const [payDialog, setPayDialog] = useState({ open: false, ticketId: null, options: null });
  const [deletingId, setDeletingId] = useState(null);

  const [fStatus, setFStatus] = useState("all");
  const [fPriority, setFPriority] = useState("all");
  const [fOrphan, setFOrphan] = useState(searchParams.get("orphan") === "1" ? "orphan" : "all");
  const [fSource, setFSource] = useState(searchParams.get("source") || "all");
  const [fType, setFType] = useState(searchParams.get("type") || "all");
  const [searchTerm, setSearchTerm] = useState(searchParams.get("q") || "");
  const [debouncedQ, setDebouncedQ] = useState(searchTerm);

  // Debounce search input to avoid hitting the API on every keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(searchTerm), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (fStatus !== "all") params.status = fStatus;
      if (fPriority !== "all") params.priority = fPriority;
      if (fOrphan === "orphan") params.is_orphan = true;
      if (fOrphan === "regular") params.is_orphan = false;
      if (fSource !== "all") params.source = fSource;
      if (fType !== "all") params.intervention_type = fType;
      if (debouncedQ && debouncedQ.trim()) params.q = debouncedQ.trim();
      const [t, d, u] = await Promise.all([
        api.get("/tickets", { params }),
        api.get("/dispensers"),
        api.get("/users"),
      ]);
      setItems(t.data);
      setDispensers(d.data);
      setUsers(u.data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [fStatus, fPriority, fOrphan, fSource, fType, debouncedQ]);

  useEffect(() => { load(); }, [load]);

  // Live warning if serial doesn't match any dispenser in DB
  useEffect(() => {
    const s = (form.dispenser_serial || "").trim();
    if (!s) { setSerialWarning(""); return; }
    const match = dispensers.some((d) => d.serial_number === s);
    setSerialWarning(match ? "" : t("tickets.form.serial_warning"));
  }, [form.dispenser_serial, dispensers, t]);

  const handleOrphanFilter = (v) => {
    setFOrphan(v);
    const next = new URLSearchParams(searchParams);
    if (v === "orphan") next.set("orphan", "1");
    else next.delete("orphan");
    setSearchParams(next, { replace: true });
  };

  const handleSourceFilter = (v) => {
    setFSource(v);
    const next = new URLSearchParams(searchParams);
    if (v !== "all") next.set("source", v);
    else next.delete("source");
    setSearchParams(next, { replace: true });
  };

  const handleTypeFilter = (v) => {
    setFType(v);
    const next = new URLSearchParams(searchParams);
    if (v !== "all") next.set("type", v);
    else next.delete("type");
    setSearchParams(next, { replace: true });
  };

  const save = async () => {
    const serial = (form.dispenser_serial || "").trim();
    if (!serial) { toast.error(t("tickets.form.error_serial_required")); return; }
    if (!form.title.trim() || !form.description.trim()) {
      toast.error(t("tickets.form.error_title_description")); return;
    }
    if (form.intervention_type === "software" && !form.license_id) {
      toast.error(t("tickets.license_required"));
      return;
    }
    try {
      const payload = {
        ...form,
        dispenser_serial: serial,
        assigned_to: form.assigned_to || null,
        license_id: form.intervention_type === "software" ? form.license_id : null,
      };
      const res = await api.post("/tickets", payload);
      if (res.data?.payment_required) {
        toast.warning(t("tickets.create_payment_pending"));
        setOpen(false);
        setForm(empty); setSelectedLicense(null);
        load();
        setPayDialog({ open: true, ticketId: res.data.id, options: res.data.payment_options });
        return;
      }
      if (res.data?.is_orphan) {
        toast.success(t("tickets.create_orphan_success"));
      } else {
        toast.success(t("tickets.create_success"));
      }
      setOpen(false); setForm(empty); setSelectedLicense(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteTicket = async (tid, ticketNumber) => {
    if (!window.confirm(t("tickets.delete_confirm", { number: ticketNumber }))) return;
    setDeletingId(tid);
    try {
      await api.delete(`/tickets/${tid}`);
      toast.success(t("tickets.delete_success", { number: ticketNumber }));
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDeletingId(null); }
  };

  return (
    <div className="p-4 sm:p-8 max-w-7xl">
      <PageHeader
        title={t("tickets.title")}
        description={t("tickets.subtitle")}
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-ticket-btn" className="bg-brand hover:bg-brand-dark text-white rounded-sm">
                <Plus className="h-4 w-4 mr-2" /> {t("tickets.new_ticket")}
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm">
              <DialogHeader>
                <DialogTitle className="font-display tracking-tight">{t("tickets.new_ticket")}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">{t("tickets.form.serial_label")} *</Label>
                  <Input
                    data-testid="ticket-dispenser-serial"
                    list="dispenser-serials"
                    value={form.dispenser_serial}
                    onChange={(e) => setForm({ ...form, dispenser_serial: e.target.value })}
                    className="mt-1 rounded-sm font-mono"
                    placeholder={t("tickets.form.serial_placeholder")}
                    autoComplete="off"
                  />
                  <datalist id="dispenser-serials">
                    {dispensers.map((d) => (
                      <option key={d.id} value={d.serial_number}>
                        {d.model}{d.customer_name ? ` · ${d.customer_name}` : ""}
                      </option>
                    ))}
                  </datalist>
                  {serialWarning && (
                    <div data-testid="serial-orphan-warning" className="mt-2 flex items-start gap-2 rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                      <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                      <span>{serialWarning}</span>
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">{t("tickets.form.title_label")} *</Label>
                  <Input data-testid="ticket-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 rounded-sm" placeholder={t("tickets.form.title_placeholder")} />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">{t("tickets.form.description_label")} *</Label>
                  <Textarea data-testid="ticket-description" rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 rounded-sm" placeholder={t("tickets.form.description_placeholder")} />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">{t("tickets.intervention_type_label")}</Label>
                  <Select value={form.intervention_type} onValueChange={(v) => setForm({ ...form, intervention_type: v })}>
                    <SelectTrigger data-testid="ticket-intervention-type" className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hardware"><span className="inline-flex items-center gap-2"><Cpu className="h-3.5 w-3.5" /> {t("tickets.intervention_hardware")}</span></SelectItem>
                      <SelectItem value="software"><span className="inline-flex items-center gap-2"><Code2 className="h-3.5 w-3.5" /> {t("tickets.intervention_software")}</span></SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="mt-1 text-[11px] text-zinc-500">{t("tickets.intervention_type_hint")}</div>
                </div>
                {form.intervention_type === "software" && (
                  <div data-testid="ticket-license-block">
                    <Label className="text-xs uppercase tracking-widest text-zinc-500">
                      {t("tickets.license_label")} <span className="text-red-600">*</span>
                    </Label>
                    <div className="mt-1">
                      <LicensePicker
                        value={selectedLicense}
                        onChange={(lic) => { setSelectedLicense(lic); setForm((f) => ({ ...f, license_id: lic?.id || "" })); }}
                        required
                      />
                    </div>
                    <div className="mt-1 text-[11px] text-zinc-500">{t("tickets.license_hint")}</div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-zinc-500">{t("tickets.form.priority_label")}</Label>
                    <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                      <SelectTrigger data-testid="ticket-priority" className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bassa">{t("tickets.priority.bassa")}</SelectItem>
                        <SelectItem value="media">{t("tickets.priority.media")}</SelectItem>
                        <SelectItem value="alta">{t("tickets.priority.alta")}</SelectItem>
                        <SelectItem value="critica">{t("tickets.priority.urgente")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-zinc-500">{t("tickets.form.assign_label")}</Label>
                    <Select value={form.assigned_to || "none"} onValueChange={(v) => setForm({ ...form, assigned_to: v === "none" ? "" : v })}>
                      <SelectTrigger data-testid="ticket-assign" className="mt-1 rounded-sm"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{t("tickets.form.assign_none")}</SelectItem>
                        {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">{t("tickets.form.reporter_label")}</Label>
                  <Input data-testid="ticket-reporter" value={form.reported_by} onChange={(e) => setForm({ ...form, reported_by: e.target.value })} className="mt-1 rounded-sm" placeholder={t("tickets.form.reporter_placeholder")} />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
                <Button data-testid="ticket-save" className="bg-brand hover:bg-brand-dark text-white rounded-sm" onClick={save}>{t("tickets.form.create")}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {user?.role === "tecnico_distributore" && (
        <div className="mb-6">
          <RicaricaOreWidget />
        </div>
      )}

      <div className="flex items-center gap-2 sm:gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-full sm:min-w-[240px] sm:max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-400 pointer-events-none" />
          <Input
            data-testid="tickets-search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t("tickets.search_placeholder")}
            className="pl-9 h-8 rounded-sm text-sm"
          />
        </div>
        <div className="hidden sm:flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500">
          <Filter className="h-3.5 w-3.5" /> {t("tickets.filters_button")}
        </div>
        <Select value={fStatus} onValueChange={setFStatus}>
          <SelectTrigger data-testid="filter-status" className="flex-1 min-w-[140px] sm:flex-none sm:w-40 h-8 rounded-sm text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("tickets.filter_all_statuses")}</SelectItem>
            <SelectItem value="aperto">{t("tickets.status.aperto")}</SelectItem>
            <SelectItem value="in_lavorazione">{t("tickets.status.in_lavorazione")}</SelectItem>
            <SelectItem value="risolto">{t("tickets.status.risolto")}</SelectItem>
            <SelectItem value="chiuso">{t("tickets.status.chiuso")}</SelectItem>
            <SelectItem value="in_attesa_pagamento">{t("tickets.status.in_attesa_cliente")}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={fPriority} onValueChange={setFPriority}>
          <SelectTrigger data-testid="filter-priority" className="flex-1 min-w-[140px] sm:flex-none sm:w-40 h-8 rounded-sm text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("tickets.filter_all_priorities")}</SelectItem>
            <SelectItem value="critica">{t("tickets.priority.urgente")}</SelectItem>
            <SelectItem value="alta">{t("tickets.priority.alta")}</SelectItem>
            <SelectItem value="media">{t("tickets.priority.media")}</SelectItem>
            <SelectItem value="bassa">{t("tickets.priority.bassa")}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={fOrphan} onValueChange={handleOrphanFilter}>
          <SelectTrigger data-testid="filter-orphan" className="flex-1 min-w-[160px] sm:flex-none sm:w-56 h-8 rounded-sm text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("tickets.filter_all_orphan")}</SelectItem>
            <SelectItem value="regular">{t("tickets.filter_only_registered")}</SelectItem>
            <SelectItem value="orphan">{t("tickets.filter_only_orphan")}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={fSource} onValueChange={handleSourceFilter}>
          <SelectTrigger data-testid="filter-source" className="flex-1 min-w-[140px] sm:flex-none sm:w-44 h-8 rounded-sm text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("tickets.filter_all_sources")}</SelectItem>
            <SelectItem value="whatsapp">📱 WhatsApp</SelectItem>
            <SelectItem value="qr_public">📷 {t("tickets.source_qr_public")}</SelectItem>
            <SelectItem value="web">💻 {t("tickets.source_web")}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={fType} onValueChange={handleTypeFilter}>
          <SelectTrigger data-testid="filter-type" className="flex-1 min-w-[140px] sm:flex-none sm:w-40 h-8 rounded-sm text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("tickets.filter_all_types")}</SelectItem>
            <SelectItem value="hardware"><span className="inline-flex items-center gap-2"><Cpu className="h-3 w-3" /> {t("tickets.intervention_hardware")}</span></SelectItem>
            <SelectItem value="software"><span className="inline-flex items-center gap-2"><Code2 className="h-3 w-3" /> {t("tickets.intervention_software")}</span></SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="rounded-sm border-zinc-200 p-0 overflow-hidden bg-white">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-200">
              <th className="text-left px-5 py-3 font-medium">{t("tickets.col_number")}</th>
              <th className="text-left px-5 py-3 font-medium">{t("tickets.col_title")}</th>
              <th className="text-left px-5 py-3 font-medium">{t("tickets.col_dispenser")}</th>
              <th className="text-left px-5 py-3 font-medium">{t("tickets.col_priority")}</th>
              <th className="text-left px-5 py-3 font-medium">{t("tickets.col_status")}</th>
              <th className="text-left px-5 py-3 font-medium">{t("tickets.col_assigned")}</th>
              <th className="text-left px-5 py-3 font-medium">{t("tickets.col_created")}</th>
              {isAdmin && <th className="text-right px-5 py-3 font-medium">{t("tickets.col_actions")}</th>}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={isAdmin ? 8 : 7} className="p-10 text-center text-zinc-500">{t("tickets.empty")}</td></tr>}
            {items.map((tk) => (
              <tr key={tk.id} className="border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer" data-testid={`ticket-row-${tk.id}`}
                onClick={() => { window.location.href = `/tickets/${tk.id}`; }}>
                <td className="px-5 py-3">
                  <Link to={`/tickets/${tk.id}`} onClick={(e) => e.stopPropagation()} className="text-brand font-mono text-xs">{tk.ticket_number}</Link>
                  {tk.source === "whatsapp" && (
                    <Badge data-testid={`wa-badge-${tk.id}`} className="ml-2 rounded-sm text-[9px] uppercase tracking-widest border bg-emerald-100 text-emerald-800 border-emerald-300 font-sans">
                      <MessageCircle className="h-2.5 w-2.5 mr-1" /> WhatsApp
                    </Badge>
                  )}
                  {tk.source === "qr_public" && (
                    <Badge data-testid={`qr-badge-${tk.id}`} className="ml-2 rounded-sm text-[9px] uppercase tracking-widest border bg-blue-50 text-blue-800 border-blue-200 font-sans">
                      <QrCode className="h-2.5 w-2.5 mr-1" /> QR
                    </Badge>
                  )}
                </td>
                <td className="px-5 py-3">
                  <Link to={`/tickets/${tk.id}`} onClick={(e) => e.stopPropagation()} className="text-zinc-900 font-medium hover:underline">{tk.title}</Link>
                  {tk.intervention_type === "software" && (
                    <Badge data-testid={`sw-badge-${tk.id}`} className="ml-2 rounded-sm text-[9px] uppercase tracking-widest border bg-violet-100 text-violet-800 border-violet-200 font-sans">
                      <Code2 className="h-2.5 w-2.5 mr-1" /> {t("tickets.intervention_software")}
                    </Badge>
                  )}
                </td>
                <td className="px-5 py-3 text-zinc-700 font-mono text-xs">
                  <div className="flex items-center gap-2">
                    <span>{tk.dispenser_serial}</span>
                    {tk.is_orphan && (
                      <Badge data-testid={`orphan-badge-${tk.id}`} className="rounded-sm text-[9px] uppercase tracking-widest border bg-orange-100 text-orange-800 border-orange-300 font-sans">
                        <AlertTriangle className="h-2.5 w-2.5 mr-1" /> {t("tickets.orphan_badge")}
                      </Badge>
                    )}
                  </div>
                  <div className="text-zinc-500 font-sans">{tk.dispenser?.model || (tk.is_orphan ? t("tickets.orphan_not_in_registry") : "")}</div>
                </td>
                <td className="px-5 py-3">
                  <Badge className={`rounded-sm text-[10px] uppercase tracking-widest border ${priorityColor[tk.priority]}`}>{t(`tickets.priority.${tk.priority}`, tk.priority)}</Badge>
                </td>
                <td className="px-5 py-3">
                  <Badge className={`rounded-sm text-[10px] uppercase tracking-widest border ${statusColor[tk.status]}`}>{t(`tickets.status.${tk.status}`, tk.status.replace("_", " "))}</Badge>
                </td>
                <td className="px-5 py-3 text-zinc-700">{tk.assigned_to_name || "—"}</td>
                <td className="px-5 py-3 text-zinc-500 text-xs">{new Date(tk.created_at).toLocaleDateString(i18n.language === "en" ? "en-GB" : "it-IT")}</td>
                {isAdmin && (
                  <td className="px-5 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteTicket(tk.id, tk.ticket_number); }}
                      disabled={deletingId === tk.id}
                      className="inline-flex items-center gap-1 h-8 px-2 rounded-sm border border-red-200 text-xs text-red-700 hover:bg-red-50 disabled:opacity-50"
                      data-testid={`ticket-delete-${tk.id}`}
                      title={t("tickets.delete_admin_tooltip")}
                    >
                      {deletingId === tk.id
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : <Trash2 className="h-3.5 w-3.5" />}
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </Card>

      <PayHoursDialog
        open={payDialog.open}
        onOpenChange={(v) => setPayDialog((p) => ({ ...p, open: v }))}
        ticketId={payDialog.ticketId}
        payment_options={payDialog.options}
      />
    </div>
  );
}

